import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ShieldCheck, Camera, Bell, Clock, Star, CheckCircle2, MapPin, ChevronLeft, ChevronRight, Activity, KeyRound, ClipboardList, Ruler, Wrench, Quote, X, ExternalLink, Home as HomeIcon, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useSettings } from "@/lib/settings";
import api from "@/lib/api";
import { HeroWidget, CctvIllustration, IntercomIllustration, AlarmIllustration } from "@/components/HeroGraphics";

const ILLUSTRATIONS = { cctv: CctvIllustration, intercom: IntercomIllustration, alarm: AlarmIllustration };

const DEFAULT_SLIDES = [
  {
    key: "cctv",
    image: "https://images.unsplash.com/photo-1554936970-e49a373f6967?auto=format&fit=crop&w=1920&q=80",
    eyebrow: "24/7 Surveillance",
    line1: "Every angle,",
    line2: "covered.",
    subtitle: "4K IP cameras, PTZ & thermal — installed, recorded and monitored around the clock.",
    illustration: "cctv",
    link: "/products?category=camera",
    widgets: [
      { icon: Camera, label: "Live Feed", value: "4 Cameras", sub: "All systems online", className: "top-14 right-2 md:right-10" },
      { icon: Activity, label: "Motion Detected", value: "Front Gate", sub: "2 min ago", className: "bottom-24 right-10 md:right-24", delay: 0.3 },
    ],
  },
  {
    key: "intercom",
    image: "https://images.unsplash.com/photo-1758210480505-253f4dc6a2fc?auto=format&fit=crop&w=1920&q=80",
    eyebrow: "Video Door Entry",
    line1: "See who's",
    line2: "at the door.",
    subtitle: "IP & 4-wire video intercoms — residential entry, apartments and gated estates.",
    illustration: "intercom",
    link: "/products?category=intercom",
    widgets: [
      { icon: Bell, label: "Visitor Call", value: "Front Door", sub: "Ringing now", className: "top-14 right-2 md:right-12" },
      { icon: KeyRound, label: "Access Log", value: "48 entries", sub: "This week", className: "bottom-24 right-8 md:right-28", delay: 0.3 },
    ],
  },
  {
    key: "alarm",
    image: "https://images.unsplash.com/photo-1757359056339-22968344cce6?auto=format&fit=crop&w=1920&q=80",
    eyebrow: "Wireless & Wired Alarms",
    line1: "Armed, and",
    line2: "always alert.",
    subtitle: "Ajax & Bosch alarm panels with photo verification and 24/7 monitored response.",
    illustration: "alarm",
    link: "/products?category=alarm",
    widgets: [
      { icon: ShieldCheck, label: "Alarm Status", value: "Armed", sub: "All zones secure", className: "top-14 right-2 md:right-10" },
      { icon: Clock, label: "Response Time", value: "4 min avg", sub: "24/7 monitored", className: "bottom-24 right-10 md:right-24", delay: 0.3 },
    ],
  },
];

const IMG_RES = "https://images.unsplash.com/photo-1780399346101-6481fec66224?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxicmlzYmFuZSUyMHJlc2lkZW50aWFsJTIwaG91c2V8ZW58MHx8fHwxNzgzNzcxNDU0fDA&ixlib=rb-4.1.0&q=85";
const IMG_IND = "https://images.unsplash.com/photo-1589935447067-5531094415d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxzdXJ2ZWlsbGFuY2UlMjBjYW1lcmElMjBpbnN0YWxsYXRpb258ZW58MHx8fHwxNzgzNzcxNDU0fDA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  { icon: Camera, title: "CCTV Cameras", body: "Professional 4K IP cameras, PTZ, thermal & LPR. Supply and install.", img: "https://images.unsplash.com/photo-1530151928300-3864d0e5d178?w=1200&q=80&auto=format&fit=crop", link: "/products?category=camera" },
  { icon: Bell, title: "Intercom Services", body: "IP & 4-wire video intercoms — residential entry, apartments, gated estates.", img: "https://images.unsplash.com/photo-1558002038-1055907df827?w=1200&q=80&auto=format&fit=crop", link: "/products?category=intercom" },
  { icon: ShieldCheck, title: "Alarm Systems", body: "Ajax & Bosch wireless alarms with 24/7 monitoring & photo verification.", img: "https://images.unsplash.com/photo-1757359056339-22968344cce6?w=1200&q=80&auto=format&fit=crop", link: "/products?category=alarm" },
];

const STEPS = [
  { n: "01", icon: ClipboardList, t: "Submit a quote", d: "Tell us about your property, upload up to 10 photos of the areas you want covered." },
  { n: "02", icon: Ruler, t: "We plan the layout", d: "Our team marks up your images with exact camera placements and sends a fixed quote." },
  { n: "03", icon: Wrench, t: "Install & monitor", d: "Licensed installers get you online. Optional 24/7 monitoring for full peace of mind." },
];

const TESTIMONIALS = [
  { name: "Sarah", location: "Bulimba", body: "Eagle Eye took over from our old alarm company. Response time is unreal, and the app is finally usable.", rating: 5 },
  { name: "James", location: "Cannon Hill", body: "They marked up photos of our warehouse showing exactly where each camera would go before we signed. Zero surprises, and the install crew were tidy and on time.", rating: 5 },
  { name: "Priya", location: "New Farm", body: "Neat cabling, tidy install, professional team. Would 100% recommend for residential.", rating: 5 },
  { name: "Marcus", location: "West End", body: "Quick to respond when a sensor tripped at 2am — had someone on the phone within a minute. Genuinely reassuring.", rating: 5 },
];

export default function HomePage() {
  const nav = useNavigate();
  const { settings } = useSettings();
  const slides = (settings?.hero_slides?.length ? settings.hero_slides : DEFAULT_SLIDES).filter(s => s.image);
  const [idx, setIdx] = useState(0);
  const [featured, setFeatured] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [blogIdx, setBlogIdx] = useState(0);
  const [featIdx, setFeatIdx] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [reviewIdx, setReviewIdx] = useState(0);
  const [openReview, setOpenReview] = useState(null);
  const [googleData, setGoogleData] = useState(null);
  const ctaRef = useRef(null);
  const [cursor, setCursor] = useState({ x: -100, y: -100, active: false });

  useEffect(() => { if (slides.length < 2) return; const id = setInterval(() => setIdx(i => (i + 1) % slides.length), 6000); return () => clearInterval(id); }, [slides.length]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then(r => setFeatured(Array.isArray(r.data) ? r.data : [])).catch(() => setFeatured([]));
    api.get("/gallery").then(r => setGallery(Array.isArray(r.data) ? r.data : [])).catch(() => setGallery([]));
    api.get("/blogs", { params: { limit: 10 } }).then(r => setBlogs(Array.isArray(r.data) ? r.data : [])).catch(() => setBlogs([]));
  }, []);
  useEffect(() => { if (blogs.length < 2) return; const id = setInterval(() => setBlogIdx(i => (i + 1) % blogs.length), 5500); return () => clearInterval(id); }, [blogs.length]);
  useEffect(() => { if (featured.length < 2) return; const id = setInterval(() => setFeatIdx(i => (i + 1) % Math.max(1, Math.ceil(featured.length / 3))), 6000); return () => clearInterval(id); }, [featured.length]);
  const liveReviews = googleData?.reviews?.length > 0
    ? googleData.reviews.map(r => ({ name: r.author_name, location: r.relative_time, body: r.text, rating: r.rating }))
    : null;
  const displayReviews = liveReviews || TESTIMONIALS;

  useEffect(() => {
    api.get("/reviews").then(r => setGoogleData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { setReviewIdx(0); const id = setInterval(() => setReviewIdx(i => (i + 1) % displayReviews.length), 5000); return () => clearInterval(id); }, [displayReviews.length]);

  const onCtaMove = (e) => {
    const rect = ctaRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCursor({ x: e.clientX - rect.left, y: e.clientY - rect.top, active: true });
  };
  const onCtaTouch = (e) => {
    const rect = ctaRef.current?.getBoundingClientRect();
    if (!rect || !e.touches?.[0]) return;
    setCursor({ x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top, active: true });
  };

  const s = slides[idx] || DEFAULT_SLIDES[0];
  const tagline = settings?.tagline;
  const featChunk = featured.slice(featIdx * 3, featIdx * 3 + 3);
  const featPages = Math.max(1, Math.ceil(featured.length / 3));

  return (
    <div>
      {/* HERO SLIDESHOW — each slide is its own theme (CCTV / Intercom / Alarm):
          own photo, own headline, own floating live-data widgets, and its
          own line-art device illustration standing in for a "hero figure". */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <AnimatePresence mode="wait">
            <motion.img
              key={idx}
              src={s.image}
              alt=""
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.2 }}
              className="w-full h-full object-cover"
            />
          </AnimatePresence>
          {/* dark tint that stays dark in both themes so hero content is always readable */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/55 to-black/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/10 to-transparent" />
        </div>

        {/* Illustration + floating widgets, right side, hidden on small screens where there's no room */}
        <div className="hidden lg:block absolute inset-y-0 right-0 w-[45%] xl:w-[40%] pointer-events-none">
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ duration: 0.7 }}
              className="relative w-full h-full"
            >
              {(() => {
                const Illustration = ILLUSTRATIONS[s.illustration];
                return Illustration ? (
                  <Illustration className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] xl:w-[480px] xl:h-[480px] opacity-90" />
                ) : null;
              })()}
              {(s.widgets || []).map((w, wi) => (
                <div key={wi} className="pointer-events-auto">
                  <HeroWidget {...w} />
                </div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-24 pb-32 lg:pt-36 lg:pb-44 relative">
          <AnimatePresence mode="wait">
            <motion.div key={idx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.6 }} className="max-w-2xl lg:max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/20 bg-white/5 text-xs text-white/80 mb-6 backdrop-blur">
                <MapPin className="h-3 w-3 text-eagle-gold" /> {s.eyebrow || "Brisbane · Queensland"}
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight text-white leading-[1.05]">
                <span className="block">{tagline ? tagline.split(".")[0] : (s.line1 || s.title)}</span>
                {!tagline && s.line2 && <span className="text-eagle-gold block">{s.line2}</span>}
              </h1>
              <p className="mt-6 text-lg text-white/70 max-w-2xl leading-relaxed">{s.subtitle}</p>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <Button size="lg" onClick={() => nav("/quote")} data-testid="hero-get-quote" className="rounded-full bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover px-7 h-12 text-base font-semibold">
                  Get a Free Quote <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => nav(s.link || "/products")} data-testid="hero-view-products" className="rounded-full h-12 px-7 border-white/30 text-white bg-white/5 hover:bg-white/10">
                  {s.key === "intercom" ? "Browse Intercoms" : s.key === "alarm" ? "Browse Alarms" : "Browse Cameras"}
                </Button>
              </div>
              <div className="mt-10 flex flex-wrap gap-6 text-sm text-white/60">
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-eagle-gold" /> Licensed & insured</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-eagle-gold" /> Visual camera plan included</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-eagle-gold" /> No surprise bills</div>
              </div>
            </motion.div>
          </AnimatePresence>
          {slides.length > 1 && (
            <div className="absolute bottom-24 md:bottom-28 left-1/2 -translate-x-1/2 flex items-center gap-4">
              <button onClick={() => setIdx((idx - 1 + slides.length) % slides.length)} className="h-9 w-9 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white flex items-center justify-center hover:bg-white/20" data-testid="hero-prev"><ChevronLeft className="h-4 w-4" /></button>
              <div className="flex gap-2">
                {slides.map((_, i) => (
                  <button key={i} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-eagle-gold" : "w-4 bg-white/30"}`} data-testid={`hero-dot-${i}`} />
                ))}
              </div>
              <button onClick={() => setIdx((idx + 1) % slides.length)} className="h-9 w-9 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white flex items-center justify-center hover:bg-white/20" data-testid="hero-next"><ChevronRight className="h-4 w-4" /></button>
            </div>
          )}
          {/* Scroll-down indicator */}
          <motion.button
            onClick={() => window.scrollBy({ top: window.innerHeight * 0.85, behavior: "smooth" })}
            aria-label="Scroll down"
            data-testid="hero-scroll-indicator"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-white/70 hover:text-white group"
          >
            <span className="text-[10px] uppercase tracking-[0.3em] font-medium">Scroll</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="h-8 w-5 rounded-full border-2 border-white/40 group-hover:border-white flex items-start justify-center pt-1.5"
            >
              <div className="h-1.5 w-1 rounded-full bg-white/70 group-hover:bg-eagle-gold" />
            </motion.div>
          </motion.button>
        </div>

        {/* Bottom curve divider — echoes a half-curve transition into the page background, with a thin gold accent line */}
        <div className="absolute -bottom-1 left-0 right-0 z-10 leading-[0]">
          <svg viewBox="0 0 1440 100" preserveAspectRatio="none" className="w-full h-[60px] md:h-[90px]">
            <path d="M0,40 C360,110 1080,-30 1440,40 L1440,100 L0,100 Z" className="fill-background" />
            <path d="M0,40 C360,110 1080,-30 1440,40" fill="none" stroke="#FFB703" strokeWidth="3" />
          </svg>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-eagle-gold font-semibold mb-3">What we do</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">End-to-end security, done right.</h2>
          </div>
          <p className="text-muted-foreground max-w-md text-base">From single-camera residential setups to multi-site commercial rollouts.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -6 }}
              transition={{ delay: i * 0.1, duration: 0.5, ease: "easeOut" }}
            >
              <Link to={s.link} className="group block relative aspect-[4/3] rounded-3xl overflow-hidden border border-border/60 shadow-sm hover:shadow-2xl hover:shadow-eagle-gold/10 transition-shadow duration-500" data-testid={`service-card-${s.title.toLowerCase().replace(/\s/g, '-')}`}>
                <img src={s.img} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/10 group-hover:from-eagle-ink/95 group-hover:via-eagle-ink/50 transition-colors duration-500" />
                <div className="absolute inset-0 p-7 flex flex-col justify-end text-white">
                  <div className="h-11 w-11 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center mb-3 group-hover:bg-eagle-gold group-hover:border-eagle-gold group-hover:-translate-y-1 transition-all duration-300">
                    <s.icon className="h-5 w-5 text-eagle-gold group-hover:text-eagle-ink transition-colors" strokeWidth={1.75} />
                  </div>
                  <div className="font-display text-2xl font-bold mb-1.5 group-hover:text-eagle-gold transition-colors">{s.title}</div>
                  <div className="text-sm text-white/75 leading-relaxed mb-3 max-w-[92%]">{s.body}</div>
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-eagle-gold opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-300">
                    Explore <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-secondary/30">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24 grid grid-cols-1 md:grid-cols-2 gap-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} whileHover={{ y: -6 }} transition={{ duration: 0.5 }} className="relative rounded-3xl overflow-hidden aspect-[4/3] group shadow-sm hover:shadow-2xl hover:shadow-eagle-gold/10 transition-shadow duration-500">
            <img src={IMG_RES} alt="Residential" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/88 via-black/35 to-black/5" />
            <div className="absolute top-6 left-6 h-11 w-11 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center group-hover:bg-eagle-gold group-hover:border-eagle-gold transition-all duration-300">
              <HomeIcon className="h-5 w-5 text-eagle-gold group-hover:text-eagle-ink transition-colors" strokeWidth={1.75} />
            </div>
            <div className="absolute bottom-0 left-0 p-8 text-white">
              <div className="text-xs uppercase tracking-widest text-eagle-gold mb-2">Residential</div>
              <div className="font-display text-3xl font-bold mb-3">Protect your family.</div>
              <p className="text-white/70 max-w-md text-sm mb-5">Discreet, professional camera systems for Brisbane homes.</p>
              <Button onClick={() => nav("/quote?type=residential")} className="rounded-full bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover">Get Residential Quote</Button>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} whileHover={{ y: -6 }} transition={{ duration: 0.5, delay: 0.1 }} className="relative rounded-3xl overflow-hidden aspect-[4/3] group shadow-sm hover:shadow-2xl hover:shadow-eagle-gold/10 transition-shadow duration-500">
            <img src={IMG_IND} alt="Commercial" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/88 via-black/35 to-black/5" />
            <div className="absolute top-6 left-6 h-11 w-11 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center group-hover:bg-eagle-gold group-hover:border-eagle-gold transition-all duration-300">
              <Building2 className="h-5 w-5 text-eagle-gold group-hover:text-eagle-ink transition-colors" strokeWidth={1.75} />
            </div>
            <div className="absolute bottom-0 left-0 p-8 text-white">
              <div className="text-xs uppercase tracking-widest text-eagle-gold mb-2">Commercial</div>
              <div className="font-display text-3xl font-bold mb-3">Protect your operation.</div>
              <p className="text-white/70 max-w-md text-sm mb-5">Multi-site CCTV, LPR, thermal & 24/7 monitoring.</p>
              <Button onClick={() => nav("/quote?type=commercial")} className="rounded-full bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover">Get Commercial Quote</Button>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="text-xs uppercase tracking-[0.2em] text-eagle-gold font-semibold mb-3">How it works</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-12 max-w-2xl">A visual quote in under 24 hours.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -4 }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              className="relative p-8 rounded-2xl bg-card border border-border hover:border-eagle-gold/50 shadow-sm hover:shadow-xl hover:shadow-eagle-gold/5 transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="h-11 w-11 rounded-xl bg-eagle-gold/10 text-eagle-gold flex items-center justify-center">
                  <s.icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <div className="font-display text-4xl font-black text-eagle-gold/25">{s.n}</div>
              </div>
              <div className="font-display text-xl font-semibold mb-2">{s.t}</div>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-eagle-gold font-semibold mb-3">Top picks</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Featured products.</h2>
            </div>
            {featPages > 1 && (
              <div className="flex items-center gap-2">
                <button onClick={() => setFeatIdx((featIdx - 1 + featPages) % featPages)} className="h-9 w-9 rounded-full border border-border hover:border-eagle-gold flex items-center justify-center" data-testid="featured-prev"><ChevronLeft className="h-4 w-4" /></button>
                <button onClick={() => setFeatIdx((featIdx + 1) % featPages)} className="h-9 w-9 rounded-full border border-border hover:border-eagle-gold flex items-center justify-center" data-testid="featured-next"><ChevronRight className="h-4 w-4" /></button>
              </div>
            )}
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={featIdx} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.4 }} className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {featChunk.map(p => (
                <motion.div key={p.product_id} whileHover={{ y: -5 }} transition={{ duration: 0.25 }}>
                  <Link to={`/products/${p.product_id}`} data-testid={`featured-${p.product_id}`}>
                    <Card className="group h-full overflow-hidden rounded-2xl border-border/70 hover:border-eagle-gold shadow-sm hover:shadow-xl hover:shadow-eagle-gold/10 transition-all duration-300">
                      <div className="aspect-square bg-secondary overflow-hidden">
                        {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />}
                      </div>
                      <div className="p-5">
                        <div className="text-xs uppercase tracking-wider text-eagle-gold font-medium mb-1">{p.brand_name}</div>
                        <div className="font-display font-semibold line-clamp-2 mb-2 group-hover:text-eagle-gold transition-colors">{p.name}</div>
                        <div className="text-sm font-semibold text-eagle-gold flex items-center gap-1">Request Quote <ArrowRight className="h-3.5 w-3.5" /></div>
                      </div>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </section>
      )}

      {/* GALLERY */}
      {gallery.length > 0 && (
        <section className="border-y border-border bg-secondary/30">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
            <div className="flex items-end justify-between gap-4 mb-8">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-eagle-gold font-semibold mb-3">Our work</div>
                <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Recent installations.</h2>
              </div>
              <p className="hidden md:block text-sm text-muted-foreground max-w-xs">Drag or scroll to explore — click any photo for the full shot.</p>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory -mx-5 px-5 lg:mx-0 lg:px-0">
              {gallery.slice(0, 12).map(g => (
                <button
                  key={g.item_id}
                  onClick={() => setLightbox(g)}
                  className="relative shrink-0 w-56 md:w-64 aspect-[4/5] rounded-2xl overflow-hidden group cursor-pointer snap-start border border-border/60 hover:border-eagle-gold transition-colors"
                  data-testid={`gallery-${g.item_id}`}
                >
                  <img src={g.image} alt={g.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 text-left text-white">
                    <div className="font-display font-semibold text-sm line-clamp-1">{g.title}</div>
                  </div>
                  <div className="absolute inset-0 bg-eagle-ink/0 group-hover:bg-eagle-ink/10 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity h-9 w-9 rounded-full bg-eagle-gold/90 flex items-center justify-center scale-90 group-hover:scale-100 duration-300">
                      <ArrowRight className="h-4 w-4 text-eagle-ink rotate-[-45deg]" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-eagle-ink border-eagle-gold/20">
          {lightbox && (
            <div>
              <img src={lightbox.image} alt={lightbox.title} className="w-full max-h-[70vh] object-contain bg-black" />
              <div className="p-6 text-white">
                <div className="font-display text-xl font-bold mb-1">{lightbox.title}</div>
                {lightbox.caption && <p className="text-sm text-white/60">{lightbox.caption}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* BLOG SLIDER */}
      {blogs.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-eagle-gold font-semibold mb-3">From the blog</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Security tips, guides &amp; news.</h2>
            </div>
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={blogIdx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.5 }}>
              <Card className="group overflow-hidden rounded-2xl border-l-4 border-l-eagle-gold hover:shadow-xl hover:shadow-eagle-gold/5 transition-shadow duration-300" data-testid={`blog-slide-${blogIdx}`}>
                <Link to={`/blog/${blogs[blogIdx]?.slug || blogs[blogIdx]?.blog_id}`} className="block p-8 md:p-12">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {(blogs[blogIdx]?.tags || []).slice(0, 3).map(t => (
                      <span key={t} className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-eagle-gold/10 text-eagle-gold border border-eagle-gold/30">{t}</span>
                    ))}
                  </div>
                  <div className="font-display text-2xl md:text-3xl font-bold mb-3 max-w-3xl group-hover:text-eagle-gold transition-colors">{blogs[blogIdx]?.title}</div>
                  <p className="text-muted-foreground max-w-3xl leading-relaxed">{blogs[blogIdx]?.excerpt}</p>
                  <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-eagle-gold">
                    Read full article <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
                <div className="px-8 md:px-12 pb-6 flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-1.5">
                    {blogs.slice(0, 8).map((_, i) => (
                      <button key={i} onClick={() => setBlogIdx(i)} className={`h-1.5 rounded-full transition-all ${i === blogIdx ? "w-6 bg-eagle-gold" : "w-2 bg-border"}`} data-testid={`blog-dot-${i}`} />
                    ))}
                  </div>
                </div>
              </Card>
            </motion.div>
          </AnimatePresence>
        </section>
      )}

      <section className="border-t border-border bg-secondary/30">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-eagle-gold font-semibold mb-3">Customer reviews</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Brisbane trusts Eagle Eye.</h2>
              {googleData?.rating > 0 && (
                <div className="flex items-center gap-2 mt-3 text-sm" data-testid="google-rating-badge">
                  <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-4 w-4 ${i < Math.round(googleData.rating) ? "fill-eagle-gold text-eagle-gold" : "text-border"}`} />)}</div>
                  <span className="font-semibold">{googleData.rating.toFixed(1)}</span>
                  <span className="text-muted-foreground">· {googleData.total_ratings} Google reviews</span>
                </div>
              )}
            </div>
            {(settings?.google_review_url || googleData?.maps_url) && (
              <a href={settings?.google_review_url || googleData?.maps_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-eagle-gold hover:underline" data-testid="google-review-link">
                See all reviews on Google <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>

          <div className="relative max-w-3xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.button
                key={reviewIdx}
                onClick={() => setOpenReview(displayReviews[reviewIdx])}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.45 }}
                className="w-full text-left p-8 md:p-10 rounded-3xl bg-card border border-border hover:border-eagle-gold/50 shadow-sm hover:shadow-xl transition-all duration-300 group"
                data-testid={`review-slide-${reviewIdx}`}
              >
                <Quote className="h-7 w-7 text-eagle-gold/40 mb-4" />
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: displayReviews[reviewIdx].rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-eagle-gold text-eagle-gold" />)}
                </div>
                <p className="text-lg leading-relaxed mb-6 line-clamp-3">&ldquo;{displayReviews[reviewIdx].body}&rdquo;</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-eagle-gold/15 text-eagle-gold font-display font-bold flex items-center justify-center text-sm">
                      {displayReviews[reviewIdx].name[0]}
                    </div>
                    <div>
                      <div className="text-sm font-semibold">{displayReviews[reviewIdx].name}</div>
                      <div className="text-xs text-muted-foreground">{displayReviews[reviewIdx].location}</div>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-eagle-gold group-hover:underline shrink-0">Read full review</span>
                </div>
              </motion.button>
            </AnimatePresence>

            <div className="flex items-center justify-center gap-2 mt-6">
              {displayReviews.map((_, i) => (
                <button key={i} onClick={() => setReviewIdx(i)} className={`h-1.5 rounded-full transition-all ${i === reviewIdx ? "w-7 bg-eagle-gold" : "w-2 bg-border"}`} data-testid={`review-dot-${i}`} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <Dialog open={!!openReview} onOpenChange={(o) => !o && setOpenReview(null)}>
        <DialogContent className="max-w-lg">
          {openReview && (
            <div>
              <div className="flex gap-0.5 mb-4">
                {Array.from({ length: openReview.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-eagle-gold text-eagle-gold" />)}
              </div>
              <p className="text-lg leading-relaxed mb-6">&ldquo;{openReview.body}&rdquo;</p>
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-eagle-gold/15 text-eagle-gold font-display font-bold flex items-center justify-center">
                  {openReview.name[0]}
                </div>
                <div>
                  <div className="text-sm font-semibold">{openReview.name}</div>
                  <div className="text-xs text-muted-foreground">{openReview.location}</div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <section
        ref={ctaRef}
        onMouseMove={onCtaMove}
        onMouseLeave={() => setCursor(c => ({ ...c, active: false }))}
        onTouchMove={onCtaTouch}
        onTouchEnd={() => setCursor(c => ({ ...c, active: false }))}
        className="relative overflow-hidden cursor-none"
      >
        <div className="absolute inset-0 grid-backdrop opacity-40" />
        {/* cursor-follow glow */}
        <div
          className="pointer-events-none absolute w-[500px] h-[500px] rounded-full blur-3xl transition-opacity duration-300"
          style={{
            left: cursor.x - 250,
            top: cursor.y - 250,
            background: "radial-gradient(closest-side, rgba(255,183,3,0.25), transparent 70%)",
            opacity: cursor.active ? 1 : 0,
          }}
        />
        {/* cursor dot */}
        <div
          className="pointer-events-none absolute rounded-full transition-opacity"
          style={{
            left: cursor.x - 8, top: cursor.y - 8, width: 16, height: 16,
            background: "#FFB703",
            boxShadow: "0 0 24px 4px rgba(255,183,3,0.7)",
            opacity: cursor.active ? 1 : 0,
          }}
        />
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-28 text-center relative">
          <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tight mb-5">Ready to secure your property?</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">Get a free, no-obligation quote with a visual camera plan. Brisbane-wide service.</p>
          <Link to="/quote">
            <Button size="lg" className="rounded-full bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover px-8 h-13 text-base font-semibold cursor-pointer" data-testid="final-cta-quote">
              Start My Quote <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}